import { useCallback, useState } from 'react';
import { getCalendars } from '@rideiq/api/services/Calendars';
import * as Constants from '@rideiq/constants';
import { useInfiniteQuery } from '@tanstack/react-query';

const CALENDARS_QUERY_KEY = 'calendars';

export const useCalendarsQuery = () => {
  const [{ query, revision }, setSearch] = useState({ query: '', revision: 0 });
  const { data, isFetching, isError, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: [CALENDARS_QUERY_KEY, query, revision],
    queryFn: async ({ pageParam }) => {
      const response = await getCalendars({
        page: pageParam,
        perPage: Constants.ITEM_LIMIT_PER_PAGE,
        ...(query ? { q: query } : {}),
      });

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load calendars');
      }

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.calendars.length === Constants.ITEM_LIMIT_PER_PAGE ? allPages.length + 1 : undefined,
  });

  const calendars = data?.pages.flatMap((page) => page.calendars) ?? [];
  const currentPage = data?.pages.length ?? 1;
  const handleQuery = useCallback((nextQuery: string) => {
    const normalizedQuery = nextQuery.trim();

    if (normalizedQuery && normalizedQuery.length < Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      return;
    }

    setSearch((previous) =>
      normalizedQuery === previous.query ? previous : { query: normalizedQuery, revision: previous.revision + 1 }
    );
  }, []);

  return {
    calendars,
    queryParams: { page: currentPage, perPage: Constants.ITEM_LIMIT_PER_PAGE, query },
    isLoading: isFetching,
    isError,
    fetchCalendars: refetch,
    handlePagination: fetchNextPage,
    handleQuery,
  };
};
